import { Card, Badge, FormControl, Row, Col } from 'react-bootstrap'
import { useRouter } from 'next/router'
import { useRef } from 'react'
import MRTFilter from './MRTFilter'
import CustomSelect from '../common/CustomSelect'
import styles from '../../styles/JobFilters.module.scss'
import { buildEventUrl } from '../../helpers/url'
import { TECH_TALENT_CATEGORIES } from '../../helpers/categories'

interface JobFiltersProps {
	occupation?: any[]
	categories?: any[]
	mrtLine?: any[]
	filters: any
	eventId: string
	urlBuilder?: (eventId: string, filters: any, mrt?: string) => string
	categoryJobCounts?: { [key: number]: number }
	totalJobs?: number
}

const defaultCategories = TECH_TALENT_CATEGORIES

const defaultOccupations = [
	{ id: 45, name: 'Business Intelligence' },
	{ id: 46, name: 'Data Engineering' },
	{ id: 47, name: 'Machine Learning (ML) Engineering' },
	{ id: 48, name: 'Data Science / AI Science' },
	{ id: 49, name: 'AI Applied Research' },
	{ id: 50, name: 'Plan & Design' },
	{ id: 51, name: 'Build and Maintain' },
	{ id: 52, name: 'Software Engineering' },
	{ id: 53, name: 'Embedded Systems Engineering' },
	{ id: 54, name: 'User Interface Design' },
	{ id: 55, name: 'Enterprise Architecture' },
	{ id: 56, name: 'Program & Project Management' },
	{ id: 57, name: 'Data Protection' },
	{ id: 58, name: 'IT Audit' },
	{ id: 59, name: 'Infrastructure Support' },
	{ id: 60, name: 'Systems Support' },
	{ id: 61, name: 'Database Support' },
	{ id: 62, name: 'Data Centre and Operations Centre Support' },
	{ id: 63, name: 'Applications Support' },
	{ id: 64, name: 'Governance Risk and Control' },
	{ id: 65, name: 'Vulnerability Assessment and Penetration Testing' },
	{ id: 66, name: 'Security Operations' },
	{ id: 67, name: 'Forensics Investigation' },
	{ id: 68, name: 'Incident Response' },
	{ id: 69, name: 'Threat Analysis' },
	{ id: 70, name: 'Security Design and Engineering' },
	{ id: 71, name: 'Pre-Sales' },
	{ id: 72, name: 'Sales' },
	{ id: 73, name: 'Customer Success' },
	{ id: 74, name: 'Marketing' },
	{ id: 75, name: 'Product Management' },
	{ id: 76, name: 'Software Development' },
	{ id: 77, name: 'Quality, Risk and Security' }
]

// const jobLevels = [
// 	{ id: 1, name: 'Entry Level' },
// 	{ id: 2, name: 'Junior Level' },
// 	{ id: 3, name: 'Mid Level' },
// 	{ id: 4, name: 'Senior Level' },
// 	{ id: 5, name: 'Executive Level' }
// ]

const jobTypes = [
	{ id: 'FT', name: 'Permanent' },
	{ id: 'CONTRACT', name: 'Contract' },
	{ id: 'FREELANCE', name: 'Freelance' },
	{ id: 'TEMPORARY', name: 'Temporary' },
	{ id: 'INTERN', name: 'Internship' },
	{ id: 'CLT', name: 'Company-Led Training' },
	{ id: 'APPRENTICESHIP', name: 'Apprenticeship' }
]

const JobFilters: React.FC<JobFiltersProps> = ({
	occupation = defaultOccupations,
	categories = defaultCategories,
	mrtLine,
	filters,
	eventId,
	urlBuilder,
	categoryJobCounts = {},
	totalJobs = 0
}) => {
	const router = useRouter()
	const salaryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	const minSalaryRef = useRef<HTMLInputElement>(null)
	const maxSalaryRef = useRef<HTMLInputElement>(null)

	const handleFilterChange = async (fieldName: string, selected: any) => {
		const ids = selected ? selected.map((s: any) => s.value) : []
		const newFilters = {
			...filters,
			[fieldName]: ids
		}
		const url = urlBuilder ? urlBuilder(eventId, newFilters) : buildEventUrl(eventId, newFilters)
		await router.push(url, undefined, { scroll: false })
	}

	const handleSalaryChange = (fieldName: string, value: string) => {
		if (salaryTimeoutRef.current) {
			clearTimeout(salaryTimeoutRef.current)
		}

		salaryTimeoutRef.current = setTimeout(async () => {
			const newFilters = {
				...filters,
				[fieldName]: value ? parseFloat(value) : null
			}
			const url = urlBuilder ? urlBuilder(eventId, newFilters) : buildEventUrl(eventId, newFilters)
			await router.push(url, undefined, { scroll: false })
		}, 300)
	}

	const formatCategoryLabel = (option: any) => (
		<div className="d-flex justify-content-between align-items-center">
			<span>{option.label}</span>
			{option.count !== undefined && <Badge bg="secondary">{option.count}</Badge>}
		</div>
	)

	return (
		<div style={{ position: 'sticky', top: '100px' }}>
			<Card className={styles.filterCard}>
				<Card.Body className={styles.filterBody}>
					<div className="d-flex justify-content-between align-items-center">
						<h5 className={styles.filterTitle} style={{ margin: 0 }}>Filter</h5>
						<Badge bg="secondary">{totalJobs || 0} jobs</Badge>
					</div>

					{categories && categories.length > 0 && (
						<div className={styles.filterSection}>
							<label className={styles.filterLabel}>Job Category</label>
							<CustomSelect
								options={categories.map(cat => ({
									value: cat.id,
									label: cat.name,
									count: categoryJobCounts[cat.id] || 0
								}))}
								placeholder="Select Job Category"
								onChange={(selected) => handleFilterChange('jobCategories', selected)}
								value={categories.filter(cat => filters.jobCategories && filters.jobCategories.includes(cat.id.toString())).map(cat => ({
									value: cat.id,
									label: cat.name
								}))}
								isMulti={true}
								customSelectedColor="#6E368D"
								formatOptionLabel={formatCategoryLabel}
							/>
						</div>
					)}

					{occupation && occupation.length > 0 && (
						<div className={styles.filterSection}>
							<label className={styles.filterLabel}>Job Skills</label>
							<CustomSelect
								options={occupation.map(occ => ({ value: occ.id, label: occ.name }))}
								placeholder="Select Job Skills"
								onChange={(selected) => handleFilterChange('jobOccupations', selected)}
								value={occupation.filter(occ => filters.jobOccupations && filters.jobOccupations.includes(occ.id.toString())).map(occ => ({ value: occ.id, label: occ.name }))}
								isMulti={true}
								customSelectedColor="#6E368D"
							/>
						</div>
					)}
					{/* <div className={styles.filterSection}>
						<label className={styles.filterLabel}>Job Level</label>
						<CustomSelect
							options={jobLevels.map(level => ({ value: level.id, label: level.name }))}
							placeholder="Select Job Level"
							onChange={(selected) => handleFilterChange('jobLevels', selected)}
							value={filters.jobLevels ? jobLevels.filter(level => filters.jobLevels.includes(level.id)).map(level => ({ value: level.id, label: level.name })) : []}
							isMulti={true}
							customSelectedColor={isTechTalentEvent(eventId) ? '#6E368D' : undefined}
						/>
					</div> */}
					<div className={styles.filterSection}>
						<label className={styles.filterLabel}>Job Type</label>
						<CustomSelect
							options={jobTypes.map(type => ({ value: type.id, label: type.name }))}
							placeholder="Select Job Type"
							onChange={(selected) => handleFilterChange('jobTypes', selected)}
							value={filters.jobTypes ? jobTypes.filter(type => filters.jobTypes.includes(type.id)).map(type => ({ value: type.id, label: type.name })) : []}
							isMulti={true}
							customSelectedColor="#6E368D"
						/>
					</div>
					<div className={styles.filterSection}>
						<label className={styles.filterLabel}>Salary Range</label>
						<Row>
							<Col xs={6}>
								<FormControl
									ref={minSalaryRef}
									type="number"
									placeholder="Min Salary"
									defaultValue={filters.minSalary || ''}
									onChange={(e) => handleSalaryChange('minSalary', e.target.value)}
									className={styles.input}
								/>
							</Col>
							<Col xs={6}>
								<FormControl
									ref={maxSalaryRef}
									type="number"
									placeholder="Max Salary"
									defaultValue={filters.maxSalary || ''}
									onChange={(e) => handleSalaryChange('maxSalary', e.target.value)}
									className={styles.input}
								/>
							</Col>
						</Row>
					</div>
					{mrtLine && mrtLine.length > 0 && (
						<MRTFilter
							mrtLine={mrtLine}
							eventId={eventId}
							filters={filters}
							urlBuilder={urlBuilder}
						/>
					)}
				</Card.Body>
			</Card>
		</div>
	)
}

export default JobFilters